import classNames from 'classnames';
import React, { FC, useState, FocusEvent, ReactNode } from 'react';

import { useInputClassNames } from '../hooks/useInputClassNames/useInputClassNames';
import { FieldProps } from '../types';
import styles from './TextField.style.m.css';

export interface ITextFieldProps extends Omit<FieldProps, 'prefix'> {
  classNameContent?: string;
  suffix?: ReactNode;
  prefix?: ReactNode | string;
}

export const TextField: FC<ITextFieldProps> = React.forwardRef((props, ref) => {
  const {
    message,
    className,
    value,
    label,
    placeholder,
    hasError,
    hasSuccess,
    isDisabled,
    onFocus,
    onBlur,
    suffix,
    prefix,
    isReadOnly,
    autoComplete = 'off',
    ...inputProps
  } = props;

  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handleFocus = (event: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(true);
    onFocus?.(event);
  };
  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(false);
    onBlur?.(event);
  };

  const {
    containerClassName,
    contentClassName,
    inputClassName,
    messageClassName,
    labelClassName,
    inputWrapperClassName,
    prefixClassName,
  } = useInputClassNames({
    isFocused,
    isLabel: !!label,
    isSuccess: hasSuccess,
    isError: hasError,
    isPrefix: !!prefix,
    isDisabled: isDisabled || isReadOnly,
    placeholder,
    value,
  });

  const containerClassNames = classNames(containerClassName, className, {
    [styles.withSuffix]: suffix,
  });
  const contentClassNames = classNames(contentClassName, styles.content);
  const inputWrapperClassNames = classNames(inputWrapperClassName, styles.inputWrapper);

  return (
    <div className={containerClassNames}>
      <div className={contentClassNames}>
        <label className={inputWrapperClassNames}>
          {label && <span className={labelClassName}>{label}</span>}
          {prefix && <span className={prefixClassName}>{prefix}</span>}
          <input
            {...inputProps}
            ref={ref}
            className={inputClassName}
            value={value}
            placeholder={placeholder}
            disabled={isDisabled}
            autoComplete={autoComplete}
            onFocus={handleFocus}
            onBlur={handleBlur}
            readOnly={isReadOnly}
          />
        </label>
        {suffix && <div className={styles.suffix}>{suffix}</div>}
      </div>
      {message && <div className={messageClassName}>{message}</div>}
    </div>
  );
});
